import React, { useState } from "react";
import "../css/devPrimaryInspector.scss";

const INDENT_PX = 16;

function JsonPrimitive({ value }) {
  if (typeof value === "string") {
    return <span className="json-string">{JSON.stringify(value)}</span>;
  }
  if (typeof value === "number") {
    return <span className="json-number">{value}</span>;
  }
  if (typeof value === "boolean") {
    return <span className="json-boolean">{String(value)}</span>;
  }
  return <span className="json-null">null</span>;
}

// Renders an array/object as a collapsible node - "open"/"close" are the literal brackets
// ("[", "]" or "{", "}"), entries are [displayKey, value] pairs (displayKey null for array
// items, since those don't get a "key": prefix).
function JsonContainer({ open, close, entries, indent }) {
  const [collapsed, setCollapsed] = useState(false);

  if (entries.length === 0) {
    return <span>{open}{close}</span>;
  }

  if (collapsed) {
    return (
      <span>
        <button type="button" className="collapseToggle" onClick={() => setCollapsed(false)}>
          ▶
        </button>{" "}
        {open}…{entries.length}…{close}
      </span>
    );
  }

  return (
    <span>
      <button type="button" className="collapseToggle" onClick={() => setCollapsed(true)}>
        ▼
      </button>{" "}
      {open}
      {entries.map(([displayKey, value], i) => (
        <div key={displayKey ?? i} style={{ paddingLeft: indent + INDENT_PX }}>
          {displayKey !== null && <span className="json-key">"{displayKey}"</span>}
          {displayKey !== null && ": "}
          <JsonNode value={value} indent={indent + INDENT_PX} />
          {i < entries.length - 1 ? "," : ""}
        </div>
      ))}
      <div style={{ paddingLeft: indent }}>{close}</div>
    </span>
  );
}

function JsonNode({ value, indent }) {
  if (Array.isArray(value)) {
    return (
      <JsonContainer
        open="["
        close="]"
        entries={value.map((item) => [null, item])}
        indent={indent}
      />
    );
  }
  if (value !== null && typeof value === "object") {
    return (
      <JsonContainer
        open="{"
        close="}"
        entries={Object.entries(value)}
        indent={indent}
      />
    );
  }
  return <JsonPrimitive value={value} />;
}

// Dev-only debugging aid - shows the current primary's raw Firestore document as a
// collapsible tree pinned to the bottom-left of the screen, so you can see exactly what's
// stored (including fields with no UI of their own, like threadId/jobTypeCategory) without
// opening the Firebase console. Never renders outside development mode or production builds.
function DevPrimaryInspector({ data }) {
  const [expanded, setExpanded] = useState(false);

  if (import.meta.env.MODE !== "development") return null;
  if (!data?.uid) return null;

  return (
    <div className={`devPrimaryInspector ${expanded ? "expanded" : "collapsed"}`}>
      <button type="button" className="toggle" onClick={() => setExpanded((prev) => !prev)}>
        {expanded ? "Hide" : "Show"} Firestore Data
      </button>
      {expanded && (
        <div className="jsonTree">
          <JsonNode value={data} indent={0} />
        </div>
      )}
    </div>
  );
}

export default DevPrimaryInspector;
